import pick from 'lodash/pick';
import {
  initiatePrivileged,
  BackgroundServiceApi,
  SharetribeApi,
  transitionPrivileged,
} from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { fetchCurrentUserHasOrdersSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { createAddress, getShippingCarriersAndRates } from '../../ducks/shipping.duck';
import { getShippingAddressID, getUserEmail } from '../../util/dataExtractors';
import { setInitialValues as setInitialValuesForPaymentMethods } from '../../ducks/paymentMethods.duck';
import { getProcess } from '../../transactions/transaction';
import { isPrivileged } from '../../transactions/transactionProcessPurchase';
import { addressKeyOf } from './checkoutShipping';
import { transformPaymentMethod } from '../../util/stripePaymentMethod';

const processName = 'default-purchase';
const transitions = getProcess(processName)?.transitions;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/CheckoutPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/CheckoutPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/CheckoutPage/SPECULATE_TRANSACTION_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

export const INITIATE_INQUIRY_REQUEST = 'app/CheckoutPage/INITIATE_INQUIRY_REQUEST';
export const INITIATE_INQUIRY_SUCCESS = 'app/CheckoutPage/INITIATE_INQUIRY_SUCCESS';
export const INITIATE_INQUIRY_ERROR = 'app/CheckoutPage/INITIATE_INQUIRY_ERROR';

export const CREATE_PAYMENT_INTENT_REQUEST = 'app/CheckoutPage/CREATE_PAYMENT_INTENT_REQUEST';
export const CREATE_PAYMENT_INTENT_SUCCESS = 'app/CheckoutPage/CREATE_PAYMENT_INTENT_SUCCESS';
export const CREATE_PAYMENT_INTENT_ERROR = 'app/CheckoutPage/CREATE_PAYMENT_INTENT_ERROR';

export const SET_SELLER_SHIPPING_ADDRESS = 'app/CheckoutPage/SET_SELLER_SHIPPING_ADDRESS';

export const SELLER_ADDRESS_REQUEST = 'app/CheckoutPage/SELLER_ADDRESS_REQUEST';
export const SELLER_ADDRESS_SUCCESS = 'app/CheckoutPage/SELLER_ADDRESS_SUCCESS';
export const SELLER_ADDRESS_ERROR = 'app/CheckoutPage/SELLER_ADDRESS_ERROR';

export const BUYER_ADDRESS_REQUEST = 'app/CheckoutPage/BUYER_ADDRESS_REQUEST';
export const BUYER_ADDRESS_SUCCESS = 'app/CheckoutPage/BUYER_ADDRESS_SUCCESS';
export const BUYER_ADDRESS_ERROR = 'app/CheckoutPage/BUYER_ADDRESS_ERROR';

export const FETCH_RATES_REQUEST = 'app/CheckoutPage/FETCH_RATES_REQUEST';
export const FETCH_RATES_SUCCESS = 'app/CheckoutPage/FETCH_RATES_SUCCESS';
export const FETCH_RATES_ERROR = 'app/CheckoutPage/FETCH_RATES_ERROR';
export const CLEAR_RATES = 'app/CheckoutPage/CLEAR_RATES';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  orderData: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  // The latest preview asked for (see SPECULATE_TRANSACTION_SUCCESS)
  speculateRequest: 0,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  initiateInquiryInProgress: false,
  initiateInquiryError: null,
  createPaymentIntentError: null,
  // Where the item ships from (the listing's address, or its seller's profile one)
  sellerShippingAddress: null,
  // The carrier's (EasyPost's) copy of where the item ships from, for rates
  sellerAddressId: null,
  sellerAddressInProgress: false,
  sellerAddressError: null,
  sellerAddressRequest: 0,
  // The carrier's verified copy of the buyer's address: the one whose key it is
  buyerAddressKey: null,
  buyerAddressId: null,
  buyerAddressInProgress: false,
  buyerAddressError: null,
  buyerAddressRequest: 0,
  // The carriers' rates between those two copies (ratesKey names the pair)
  rates: [],
  ratesKey: null,
  fetchRatesInProgress: false,
  fetchRatesError: null,
  ratesRequest: 0,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SET_SELLER_SHIPPING_ADDRESS:
      return { ...state, sellerShippingAddress: payload };

    // Each answer below counts only when it is for the latest request: an earlier one
    // answering late (the buyer chose another address meanwhile) is dropped
    case SELLER_ADDRESS_REQUEST:
      return {
        ...state,
        sellerAddressInProgress: true,
        sellerAddressError: null,
        sellerAddressId: null,
        sellerAddressRequest: payload,
      };
    case SELLER_ADDRESS_SUCCESS:
      return action.meta?.request !== state.sellerAddressRequest
        ? state
        : { ...state, sellerAddressInProgress: false, sellerAddressId: payload };
    case SELLER_ADDRESS_ERROR:
      return action.meta?.request !== state.sellerAddressRequest
        ? state
        : { ...state, sellerAddressInProgress: false, sellerAddressError: payload };

    case BUYER_ADDRESS_REQUEST:
      return {
        ...state,
        buyerAddressInProgress: true,
        buyerAddressError: null,
        buyerAddressId: null,
        buyerAddressKey: payload.key,
        buyerAddressRequest: payload.request,
      };
    case BUYER_ADDRESS_SUCCESS:
      return action.meta?.request !== state.buyerAddressRequest
        ? state
        : { ...state, buyerAddressInProgress: false, buyerAddressId: payload };
    case BUYER_ADDRESS_ERROR:
      return action.meta?.request !== state.buyerAddressRequest
        ? state
        : { ...state, buyerAddressInProgress: false, buyerAddressError: payload };

    case FETCH_RATES_REQUEST:
      return {
        ...state,
        fetchRatesInProgress: true,
        fetchRatesError: null,
        ratesRequest: payload,
      };
    case FETCH_RATES_SUCCESS:
      return action.meta?.request !== state.ratesRequest
        ? state
        : {
            ...state,
            fetchRatesInProgress: false,
            rates: payload.rates,
            ratesKey: payload.key,
          };
    case FETCH_RATES_ERROR:
      return action.meta?.request !== state.ratesRequest
        ? state
        : { ...state, fetchRatesInProgress: false, fetchRatesError: payload };
    // Another address: its rates are asked for again (one still on its way is dropped)
    case CLEAR_RATES:
      return {
        ...state,
        rates: [],
        ratesKey: null,
        fetchRatesInProgress: false,
        fetchRatesError: null,
        ratesRequest: payload,
      };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
        speculatedTransaction: null,
        speculateRequest: payload?.request ?? state.speculateRequest,
      };
    // Only the latest preview counts: an earlier one answering late (a carrier or
    // delivery method changed meanwhile) would show a total the order won't charge
    case SPECULATE_TRANSACTION_SUCCESS:
      return (action.meta?.request ?? 0) !== state.speculateRequest
        ? state
        : {
            ...state,
            speculateTransactionInProgress: false,
            speculatedTransaction: payload.transaction,
          };
    case SPECULATE_TRANSACTION_ERROR:
      return (action.meta?.request ?? 0) !== state.speculateRequest
        ? state
        : { ...state, speculateTransactionInProgress: false, speculateTransactionError: payload };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    case INITIATE_INQUIRY_REQUEST:
      return { ...state, initiateInquiryInProgress: true, initiateInquiryError: null };
    case INITIATE_INQUIRY_SUCCESS:
      return { ...state, initiateInquiryInProgress: false };
    case INITIATE_INQUIRY_ERROR:
      return { ...state, initiateInquiryInProgress: false, initiateInquiryError: payload };

    case CREATE_PAYMENT_INTENT_REQUEST:
      return { ...state, createPaymentIntentError: null };
    case CREATE_PAYMENT_INTENT_SUCCESS:
      return state;
    case CREATE_PAYMENT_INTENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, createPaymentIntentError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = order => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = orderId => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = (request = 0) => ({
  type: SPECULATE_TRANSACTION_REQUEST,
  payload: { request },
});

export const speculateTransactionSuccess = (transaction, request = 0) => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
  meta: { request },
});

export const speculateTransactionError = (e, request = 0) => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
  meta: { request },
});

let speculateRequests = 0;

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

export const initiateInquiryRequest = () => ({ type: INITIATE_INQUIRY_REQUEST });
export const initiateInquirySuccess = () => ({ type: INITIATE_INQUIRY_SUCCESS });
export const initiateInquiryError = e => ({
  type: INITIATE_INQUIRY_ERROR,
  error: true,
  payload: e,
});

/* ================ Thunks ================ */

export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(initiateOrderRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const {
    deliveryMethod,
    quantity,
    bookingDates,
    cartItems,
    protectedData,
    shippingParams,
    ...otherOrderParams
  } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const { billingCountry, ...restOrderParams } = otherOrderParams;
  const orderData = deliveryMethod
    ? { deliveryMethod, cartItems, protectedData, shippingParams, billingCountry }
    : { cartItems, protectedData, shippingParams, billingCountry };

  // Parameters for Marketplace API (exclude billingCountry — it's only for our server)
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...restOrderParams,
    // Pass protectedData directly to Marketplace API so it gets saved to transaction
    protectedData: protectedData || {},
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSucces = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    return order;
  };

  const handleError = e => {
    dispatch(initiateOrderError(storableError(e)));
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    throw e;
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSucces)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transition(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSucces)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiate(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  }
};

// How long the page waits for the payment to be confirmed, and how often it looks.
const PAYMENT_CONFIRMATION_WAIT_MS = 45000;
const PAYMENT_CONFIRMATION_POLL_MS = 1500;
const WAITING_FOR_PAYMENT = ['state/pending-payment', 'state/offer-accepted'];

/**
 * Waits for the order's payment to be confirmed.
 *
 * The browser does not confirm a payment (process v76): the TradMarket API
 * does, once Stripe reports the money for the order's exact total. That is
 * usually a second or two after the card is charged, so the page looks at the
 * order until it has moved on. If it takes longer, the buyer goes to the order
 * page all the same, where the order shows as "payment processing" until it
 * is confirmed.
 *
 * Resolves with the order. Rejects if the order expired unpaid.
 */
export const confirmPayment = (
  transactionId,
  { waitMs = PAYMENT_CONFIRMATION_WAIT_MS, pollMs = PAYMENT_CONFIRMATION_POLL_MS } = {}
) => (dispatch, getState, sdk) => {
  dispatch(confirmPaymentRequest());
  const deadline = Date.now() + waitMs;

  const look = () =>
    sdk.transactions
      .show({ id: transactionId, include: ['booking', 'provider'] }, { expand: true })
      .then(response => {
        const order = response.data.data;
        const state = order.attributes.state;
        if (state === 'state/payment-expired') {
          const expired = new Error('The order expired before its payment was confirmed');
          expired.name = 'PaymentNotConfirmed';
          throw expired;
        }
        if (!WAITING_FOR_PAYMENT.includes(state) || Date.now() >= deadline) {
          return order;
        }
        return new Promise(resolve => setTimeout(resolve, pollMs)).then(look);
      });

  return look()
    .then(order => {
      dispatch(confirmPaymentSuccess(order.id));
      return order;
    })
    .catch(e => {
      dispatch(confirmPaymentError(storableError(e)));
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      log.error(e, 'payment-confirmation-failed', {
        ...transactionIdMaybe,
      });
      throw e;
    });
};

export const sendMessage = params => (dispatch, getState, sdk) => {
  const message = params.message;
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

/**
 * Initiate transaction against default-inquiry process
 * Note: At this point inquiry transition is made directly against Marketplace API.
 *       So, client app's server is not involved here unlike with transitions including payments.
 *
 * @param {*} inquiryParams contains listingId and protectedData
 * @param {*} processAlias 'default-inquiry/release-1'
 * @param {*} transitionName 'transition/inquire-without-payment'
 * @returns
 */
export const initiateInquiryWithoutPayment = (inquiryParams, processAlias, transitionName) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateInquiryRequest());

  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: inquiryParams?.listingId?.uuid,
    });
    dispatch(initiateInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: inquiryParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };

  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(response => {
      const transactionId = response.data.data.id;
      dispatch(initiateInquirySuccess());
      return transactionId;
    })
    .catch(e => {
      dispatch(initiateInquiryError(storableError(e)));
      throw e;
    });
};

const createPaymentIntentRequest = () => ({ type: CREATE_PAYMENT_INTENT_REQUEST });

const createPaymentIntentSuccess = data => ({
  type: CREATE_PAYMENT_INTENT_SUCCESS,
  payload: data,
});

const createPaymentIntentError = e => ({
  type: CREATE_PAYMENT_INTENT_ERROR,
  error: true,
  payload: e,
});

/**
 * Where the item ships from: the listing's own address, or (listings from before
 * listings had one) the seller's profile address. Unknown stays null.
 */
export const loadSellerShippingAddress = listing => dispatch => {
  const fromListing = listing?.attributes?.publicData?.shippingAddress;
  const set = address => dispatch({ type: SET_SELLER_SHIPPING_ADDRESS, payload: address || null });
  set(null);
  if (fromListing?.country) {
    set(fromListing);
    return Promise.resolve();
  }
  const authorId = listing?.author?.id?.uuid;
  if (!authorId) return Promise.resolve();
  return SharetribeApi.getUserShippingDetails(authorId)
    .then(({ statusCode, data }) => set(statusCode === 200 ? data : null))
    .catch(e => log.error(e, 'seller-shipping-address-failed', { authorId }));
};

/**
 * Create Payment Intent via TradMarket API, once the order is created. The server
 * works out the amount from the orders themselves.
 *
 * @param {Object} params - Payment intent parameters
 * @param {string} params.transactionId - The order's ID (or params.transactionIds for several)
 * @param {string} params.customerId - The buyer's Stripe customer, when the card is to be saved
 * @returns {Promise} Promise that resolves with the client secret
 */
export const createPaymentIntentAction = params => (dispatch, getState, sdk) => {
  dispatch(createPaymentIntentRequest());

  const { transactionId, transactionIds, customerId } = params;

  // Call local server endpoint which proxies to TradMarket API
  // This keeps the TradMarket API URL secure on the server side
  // Using api.js helper which handles the correct port in development
  return BackgroundServiceApi.createPaymentIntent({
    transactionId,
    transactionIds,
    customerId, // Pass customer ID for saving payment method
  })
    .then(response => {
      dispatch(createPaymentIntentSuccess(response));
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(createPaymentIntentError(error));
      log.error(e, 'create-payment-intent-failed', params);
      throw error;
    });
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
export const speculateTransaction = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  speculateRequests += 1;
  const request = speculateRequests;
  dispatch(speculateTransactionRequest(request));

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const {
    deliveryMethod,
    quantity,
    cartItems,
    bookingDates,
    shippingRateId,
    shippingParams,
    shipmentId,
    shippingPrice,
    protectedData,
    ...otherOrderParams
  } = orderParams;

  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const orderData = deliveryMethod
    ? {
        deliveryMethod,
        cartItems,
        shippingRateId,
        shippingParams,
        shipmentId,
        shippingPrice,
        protectedData,
      }
    : {
        cartItems,
        shippingRateId,
        shippingParams,
        shipmentId,
        shippingPrice,
        protectedData,
      };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
    // Pass protectedData directly to Marketplace API so it gets saved to transaction
    protectedData: protectedData || {},
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx, request));
  };

  const handleError = e => {
    log.error(e, 'speculate-transaction-failed', {
      listingId: transitionParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    return dispatch(speculateTransactionError(storableError(e), request));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());

  // Fetch current user to get stripeCustomerId from protectedData
  return dispatch(fetchCurrentUser())
    .then(response => {
      const user = getState().user.currentUser;
      const customerId = user?.attributes?.profile?.protectedData?.stripeCustomerId;

      if (customerId) {
        // Fetch payment methods from our TradMarket API
        return BackgroundServiceApi.listPaymentMethods(customerId)
          .then(pmResponse => {
            // Store payment methods in Redux
            const stripeCustomer = {
              id: customerId,
              attributes: {
                stripeCustomerId: customerId,
              },
              defaultPaymentMethod: transformPaymentMethod(pmResponse.paymentMethods?.[0]),
            };

            // Update payment methods state
            dispatch(setInitialValuesForPaymentMethods({ stripeCustomer }));
            dispatch(stripeCustomerSuccess());
          })
          .catch(e => {
            // If fetching payment methods fails, continue without them
            log.error(storableError(e), 'fetch-payment-methods-failed');
            dispatch(stripeCustomerSuccess());
          });
      } else {
        // No customer ID yet, user hasn't saved a payment method
        dispatch(stripeCustomerSuccess());
      }
    })
    .catch(e => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};

// ================ Shipping addresses and rates ================ //

const unavailable = name => {
  const error = new Error(name);
  error.name = name;
  return storableError(error);
};

/**
 * The carrier's copy of where the item ships from: made from the listing's own address,
 * or (a listing without one, or one the carrier won't take) the seller's profile copy.
 * Resolves with its id, or null when there is neither (the page says so, and asks again
 * when the buyer continues).
 *
 * @param {Object} params.listing the listing being bought (with its author)
 * @param {String} params.name the seller's name on the address
 */
let sellerAddressRequests = 0;
let sellerAddressPending = null;
export const prepareSellerAddress = ({ listing, name }) => (dispatch, getState) => {
  // Already on its way for this listing: the same answer
  const listingId = listing?.id?.uuid;
  if (
    sellerAddressPending &&
    sellerAddressPending.listingId === listingId &&
    getState().CheckoutPage.sellerAddressRequest === sellerAddressPending.request
  ) {
    return sellerAddressPending.promise;
  }
  sellerAddressRequests += 1;
  const request = sellerAddressRequests;
  dispatch({ type: SELLER_ADDRESS_REQUEST, payload: request });

  const author = listing?.author;
  const from = listing?.attributes?.publicData?.shippingAddress;
  const created = from
    ? dispatch(
        createAddress({
          street1: from.line1,
          street2: from.line2 || '',
          city: from.city,
          zip: from.postal_code,
          state: from.state,
          country: from.country,
          phone: from.phone_number || '',
          email: getUserEmail(author),
          name,
        })
      ).catch(() => null)
    : Promise.resolve(null);

  const promise = created.then(id => {
    if (sellerAddressPending?.request === request) sellerAddressPending = null;
    const addressId = id || getShippingAddressID(author) || null;
    if (addressId) {
      dispatch({ type: SELLER_ADDRESS_SUCCESS, payload: addressId, meta: { request } });
    } else {
      dispatch({
        type: SELLER_ADDRESS_ERROR,
        error: true,
        payload: unavailable('SellerAddressUnavailable'),
        meta: { request },
      });
    }
    return addressId;
  });
  sellerAddressPending = { listingId, request, promise };
  return promise;
};

/**
 * The carrier's verified copy of the buyer's delivery address. Resolves with its id, or
 * null when the carrier can't verify it (an address the buyer chose to use anyway is
 * taken as it is). The same address is verified once.
 *
 * @param {Object} address the delivery address (line1, city, postal_code, ...)
 * @param {Object} contact the buyer's { name, email } on it
 */
let buyerAddressRequests = 0;
let buyerAddressPending = null;
export const verifyBuyerAddress = (address, { name, email }) => (dispatch, getState) => {
  const key = addressKeyOf(address);
  if (!key) return Promise.resolve(null);

  const { buyerAddressKey, buyerAddressId } = getState().CheckoutPage;
  if (buyerAddressKey === key && buyerAddressId) {
    return Promise.resolve(buyerAddressId);
  }
  if (buyerAddressPending?.key === key && buyerAddressKey === key) {
    return buyerAddressPending.promise;
  }

  buyerAddressRequests += 1;
  const request = buyerAddressRequests;
  dispatch({ type: BUYER_ADDRESS_REQUEST, payload: { key, request } });

  const promise = dispatch(
    createAddress({
      street1: address.line1,
      street2: address.line2 || '',
      city: address.city,
      zip: address.postal_code,
      state: address.state,
      country: address.country,
      phone: address.phone_number || address.phone || '',
      email,
      name,
      allowUnverified: !!address.addressOverride,
    })
  )
    .catch(() => null)
    .then(id => {
      if (buyerAddressPending?.request === request) buyerAddressPending = null;
      if (id) {
        dispatch({ type: BUYER_ADDRESS_SUCCESS, payload: id, meta: { request } });
      } else {
        dispatch({
          type: BUYER_ADDRESS_ERROR,
          error: true,
          payload: unavailable('AddressNotVerified'),
          meta: { request },
        });
      }
      return id || null;
    });
  buyerAddressPending = { key, request, promise };
  return promise;
};

/**
 * The carriers' rates for the order, between the two carrier addresses (params.from_address,
 * params.to_address). The rates for the pair already asked for are kept. Resolves with the
 * rates, or null when another address was chosen meanwhile; rejects when they can't be had.
 */
let ratesRequests = 0;
export const fetchCheckoutRates = params => (dispatch, getState) => {
  const key = `${params?.to_address}-${params?.from_address}`;
  const { rates, ratesKey } = getState().CheckoutPage;
  if (ratesKey === key && rates.length > 0) {
    return Promise.resolve(rates);
  }

  ratesRequests += 1;
  const request = ratesRequests;
  dispatch({ type: FETCH_RATES_REQUEST, payload: request });
  const isLatest = () => getState().CheckoutPage.ratesRequest === request;

  return dispatch(getShippingCarriersAndRates(params)).then(
    found => {
      const list = Array.isArray(found) ? found : [];
      dispatch({ type: FETCH_RATES_SUCCESS, payload: { rates: list, key }, meta: { request } });
      return isLatest() ? list : null;
    },
    e => {
      dispatch({
        type: FETCH_RATES_ERROR,
        error: true,
        payload: storableError(e),
        meta: { request },
      });
      if (!isLatest()) return null;
      throw e;
    }
  );
};

/** The buyer chose another address: the rates are for it, once asked for. */
export const clearCheckoutRates = () => dispatch => {
  ratesRequests += 1;
  dispatch({ type: CLEAR_RATES, payload: ratesRequests });
};
